import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  type Tone,
} from '@zt/ui';
import { api } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { fieldError, useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';

/**
 * Talep tahmini (F9-03, ADR-128): varyant bazında haftalık satıştan tahmin, stok yeterliliği, önerilen sipariş ve
 * son kullanma tarihi riski. Yapay zekâ sağlayıcısı kullanmaz; satın alma emri oluşturmaz. Açık satın alma
 * siparişlerindeki teslim alınmamış adet öneriden düşülür, tedarik süresi tedarikçiden gelebilir (F9-13, ADR-153).
 */

type Status = 'stockout' | 'reorder' | 'on_order' | 'ok' | 'overstock' | 'no_sales';
type LeadSource = 'variant' | 'supplier' | 'default';
type Filter = Status | 'expiry_risk';
interface Row {
  variantId: string;
  productTitle: string;
  variantTitle: string;
  sku: string;
  sold28: number;
  sold90: number;
  method: 'none' | 'new' | 'ses' | 'croston';
  dailyForecast: number;
  forecast30: number;
  available: number;
  onOrder: number;
  requested: number;
  leadTimeDays: number;
  leadTimeSource: LeadSource;
  supplierLeadTimeDays: number | null;
  supplier: { id: string; name: string } | null;
  safetyDays: number;
  customSafety: boolean;
  custom: boolean;
  excluded: boolean;
  status: Status;
  daysOfCover: number | null;
  reorderPoint: number;
  suggestedQty: number;
  expiryRisks: Array<{ lotNo: string; expiryDate: string; quantity: number; atRisk: number }>;
}
interface Settings {
  historyWeeks: number;
  leadTimeDays: number;
  safetyDays: number;
  reviewDays: number;
  overstockDays: number;
  includeB2b: boolean;
}
interface Result {
  settings: Settings;
  counts: Record<Filter, number>;
  accuracy: { variants: number; wape: number | null };
  totalSuggested: number;
  totalOnOrder: number;
  items: Row[];
}

const TONE: Record<Status, Tone> = {
  stockout: 'rose',
  reorder: 'amber',
  on_order: 'pine',
  ok: 'moss',
  overstock: 'slate',
  no_sales: 'neutral',
};
const FILTERS: Filter[] = ['stockout', 'reorder', 'on_order', 'expiry_risk', 'overstock', 'ok', 'no_sales'];

export function ForecastPage() {
  const t = useT();
  const { formatNumber, formatDate } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('inventory.stock.write');
  const [status, setStatus] = useState<Filter | ''>('');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState<Row | null>(null);
  const params = new URLSearchParams({ ...(status ? { status } : {}), ...(q.trim() ? { q: q.trim() } : {}) });
  const query = useQuery({
    queryKey: ['forecast', status, q.trim()],
    queryFn: () => api.get<Result>(`/v1/admin/forecast${params.size ? `?${params}` : ''}`),
  });
  const r = query.data;
  return (
    <PageFrame
      helpKey="page.ai_forecast"
      actions={
        <a
          className="inline-flex h-10 items-center gap-2 rounded-sm border border-line-strong bg-surface px-4 text-[0.9375rem] font-medium hover:bg-surface-2"
          href={`/v1/admin/forecast/export.csv${params.size ? `?${params}` : ''}`}
        >
          <Download className="h-4 w-4" aria-hidden />
          {t('ai.forecast.csv')}
        </a>
      }
    >
      <p className="max-w-[70ch] text-sm text-ink-2">{t('ai.forecast.lead')}</p>
      {!r ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <ul className="flex flex-wrap gap-2" aria-label={t('ai.forecast.filterStatus')}>
            {FILTERS.map((f) => (
              <li key={f}>
                <button
                  type="button"
                  aria-pressed={status === f}
                  onClick={() => setStatus(status === f ? '' : f)}
                  className="rounded-sm border border-line px-3 py-1.5 text-sm hover:bg-surface-2 aria-pressed:border-pine aria-pressed:bg-pine-tint"
                >
                  {t(`ai.forecast.counts.${f}`)}:{' '}
                  <span className="font-medium tabular-nums">{r.counts[f]}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="inline-flex flex-wrap items-center gap-1 text-sm text-ink-2">
            {r.accuracy.wape === null
              ? t('ai.forecast.accuracyNone')
              : t('ai.forecast.accuracy', {
                  pct: formatNumber(r.accuracy.wape * 100, 1),
                  count: r.accuracy.variants,
                })}
            <HelpTip k="forecast.accuracy" />
            <span className="ms-2">
              · {t('ai.forecast.totalSuggested', { count: formatNumber(r.totalSuggested) })}
            </span>
            {r.totalOnOrder > 0 && (
              <span className="ms-2">
                · {t('ai.forecast.totalOnOrder', { count: formatNumber(r.totalOnOrder) })}
              </span>
            )}
          </p>
          <Section title={t('ai.forecast.product')} action={<HelpTip k="forecast.table" />}>
            <div className="mb-3 flex flex-wrap gap-3">
              <Field label={t('ai.forecast.search')} className="min-w-[16rem] flex-1">
                {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
              </Field>
              <Field label={t('ai.forecast.filterStatus')} className="w-56">
                {(p) => (
                  <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as Filter | '')}>
                    <option value="">{t('ai.forecast.all')}</option>
                    {FILTERS.map((f) => (
                      <option key={f} value={f}>
                        {t(`ai.forecast.counts.${f}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            {!r.items.length ? (
              <EmptyState title={t('ai.forecast.empty')} description={t('ai.forecast.emptyLead')} />
            ) : (
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[60rem] text-sm">
                  <caption className="sr-only">{t('ai.nav.forecast')}</caption>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.forecast.product')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.forecast.sold28')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.forecast.daily')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.forecast.available')}</th>
                      <th className="px-3 py-2 text-end font-medium">
                        <span className="inline-flex items-center gap-1">
                          {t('ai.forecast.onOrder')}
                          <HelpTip k="forecast.on_order" />
                        </span>
                      </th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.forecast.cover')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.forecast.suggested')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.forecast.status')}</th>
                      {canWrite && <th className="px-3 py-2" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {r.items.map((i) => (
                      <tr key={i.variantId}>
                        <td className="px-3 py-2">
                          <p className="font-medium">
                            {i.productTitle}
                            {i.variantTitle ? (
                              <span className="font-normal text-ink-3"> · {i.variantTitle}</span>
                            ) : null}
                          </p>
                          <p className="text-ink-3">
                            <code className="text-[0.8125rem]">{i.sku}</code> ·{' '}
                            {t(`ai.forecast.methods.${i.method}`)}
                          </p>
                          {i.expiryRisks.map((x) => (
                            <p key={x.lotNo} className="text-amber">
                              {t('ai.forecast.expiry', {
                                lot: x.lotNo,
                                date: formatDate(`${x.expiryDate}T12:00:00Z`, false),
                                count: x.atRisk,
                              })}
                            </p>
                          ))}
                          {!i.excluded && (
                            <p className="text-ink-3">
                              {t('ai.forecast.leadLine', {
                                days: i.leadTimeDays,
                                source: i.supplier
                                  ? t('ai.forecast.leadSources.supplierNamed', { name: i.supplier.name })
                                  : t(`ai.forecast.leadSources.${i.leadTimeSource}`),
                              })}
                            </p>
                          )}
                          {(i.excluded || i.customSafety) && (
                            <p className="text-ink-3">
                              {i.excluded
                                ? t('ai.forecast.excluded')
                                : t('ai.forecast.customSafety', { days: i.safetyDays })}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">{formatNumber(i.sold28)}</td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {formatNumber(i.dailyForecast, 2)}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">{formatNumber(i.available)}</td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {i.onOrder || i.requested ? (
                            <>
                              {formatNumber(i.onOrder)}
                              {i.requested > 0 && (
                                <span className="block text-ink-3">
                                  {t('ai.forecast.requestedNote', { count: formatNumber(i.requested) })}
                                </span>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {i.daysOfCover === null ? '—' : t('ai.forecast.coverDays', { days: i.daysOfCover })}
                        </td>
                        <td className="px-3 py-2 text-end font-medium tabular-nums">
                          {i.suggestedQty ? formatNumber(i.suggestedQty) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={TONE[i.status]}>{t(`ai.forecast.counts.${i.status}`)}</Badge>
                        </td>
                        {canWrite && (
                          <td className="px-3 py-2 text-end">
                            <Button size="sm" variant="ghost" onClick={() => setEdit(i)}>
                              {t('ai.forecast.adjust')}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
          {canWrite && <DefaultsSection key={JSON.stringify(r.settings)} s={r.settings} />}
          {edit && <VariantDialog row={edit} defaults={r.settings} onClose={() => setEdit(null)} />}
        </>
      )}
    </PageFrame>
  );
}

function VariantDialog({ row, defaults, onClose }: { row: Row; defaults: Settings; onClose: () => void }) {
  const t = useT();
  const [lead, setLead] = useState(row.leadTimeSource === 'variant' ? String(row.leadTimeDays) : '');
  const [safety, setSafety] = useState(row.customSafety ? String(row.safetyDays) : '');
  const [excluded, setExcluded] = useState(row.excluded);
  const save = useApiMutation(
    (body: Record<string, unknown>) => api.put(`/v1/admin/forecast/variants/${row.variantId}`, body),
    {
      invalidate: [['forecast']],
      success: t('ai.forecast.variantSaved'),
      onSuccess: onClose,
    },
  );
  const n = (s: string) => (s.trim() === '' ? null : Number(s));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('ai.forecast.variantTitle', { product: row.productTitle })}
      closeLabel={t('common.close')}
      footer={
        <Button
          variant="primary"
          loading={save.isPending}
          onClick={() => save.mutate({ leadTimeDays: n(lead), safetyDays: n(safety), excluded })}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('ai.forecast.leadTime')}
          description={
            row.supplierLeadTimeDays !== null
              ? t('ai.forecast.supplierHint', { days: row.supplierLeadTimeDays })
              : t('ai.forecast.defaultHint', { days: defaults.leadTimeDays })
          }
          error={fieldError(save.error, 'leadTimeDays')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              max={365}
              value={lead}
              onChange={(e) => setLead(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('ai.forecast.safety')}
          description={t('ai.forecast.defaultHint', { days: defaults.safetyDays })}
          error={fieldError(save.error, 'safetyDays')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              max={365}
              value={safety}
              onChange={(e) => setSafety(e.target.value)}
            />
          )}
        </Field>
        <div className="flex items-center gap-3">
          <Switch checked={excluded} onCheckedChange={setExcluded} label={t('ai.forecast.exclude')} />
          <span className="text-sm">{t('ai.forecast.exclude')}</span>
        </div>
      </div>
    </Dialog>
  );
}

function DefaultsSection({ s }: { s: Settings }) {
  const t = useT();
  const [form, setForm] = useState({
    historyWeeks: String(s.historyWeeks),
    leadTimeDays: String(s.leadTimeDays),
    safetyDays: String(s.safetyDays),
    reviewDays: String(s.reviewDays),
    overstockDays: String(s.overstockDays),
    includeB2b: s.includeB2b,
  });
  const save = useApiMutation(
    (body: Record<string, unknown>) => api.put('/v1/admin/forecast/settings', body),
    {
      invalidate: [['forecast']],
      success: t('ai.forecast.saved'),
    },
  );
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const num = (
    k: 'historyWeeks' | 'leadTimeDays' | 'safetyDays' | 'reviewDays' | 'overstockDays',
    label: string,
    hint?: string,
  ) => (
    <Field label={label} description={hint} error={fieldError(save.error, k)}>
      {(p) => <Input {...p} type="number" value={form[k]} onChange={(e) => set(k, e.target.value)} />}
    </Field>
  );
  return (
    <Section title={t('ai.forecast.settingsTitle')} action={<HelpTip k="forecast.settings" />}>
      <div className="grid gap-4 sm:grid-cols-2">
        {num('historyWeeks', t('ai.forecast.historyWeeks'))}
        {num('leadTimeDays', t('ai.forecast.defaultLead'))}
        {num('safetyDays', t('ai.forecast.defaultSafety'))}
        {num('reviewDays', t('ai.forecast.reviewDays'), t('ai.forecast.reviewHint'))}
        {num('overstockDays', t('ai.forecast.overstockDays'))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Switch
          checked={form.includeB2b}
          onCheckedChange={(v) => set('includeB2b', v)}
          label={t('ai.forecast.includeB2b')}
        />
        <span className="text-sm">{t('ai.forecast.includeB2b')}</span>
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          loading={save.isPending}
          onClick={() =>
            save.mutate({
              historyWeeks: Number(form.historyWeeks),
              leadTimeDays: Number(form.leadTimeDays),
              safetyDays: Number(form.safetyDays),
              reviewDays: Number(form.reviewDays),
              overstockDays: Number(form.overstockDays),
              includeB2b: form.includeB2b,
            })
          }
        >
          {t('ai.forecast.save')}
        </Button>
      </div>
    </Section>
  );
}
